import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";

const METRICS = [
  { value: 200, suffix: "M+", label: "Connected TV Homes", testid: "metric-homes" },
  { value: 193, suffix: "", label: "Broadcast Countries", testid: "metric-countries" },
  { value: 100, suffix: "+", label: "Gold & Platinum Records", testid: "metric-records" },
  { value: 4, suffix: "", label: "Grammy Nominations", testid: "metric-grammys" },
];

const PLATFORMS = [
  "ROKU", "APPLE TV", "FIRE TV", "ANDROID TV", "SAMSUNG TIZEN",
  "LG WEBOS", "SONY", "HISENSE", "TCL", "VIZIO",
];

const CountUp = ({ to, suffix, testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [v, setV] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 2.2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (x) => setV(Math.round(x)),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref} data-testid={testid} className="font-head font-extrabold tabular-nums text-4xl sm:text-5xl lg:text-6xl text-white">
      {v}
      <span className="text-signal">{suffix}</span>
    </span>
  );
};

export default function Reach() {
  return (
    <section id="reach" className="relative py-24 lg:py-32 px-4 sm:px-8 lg:px-16 bg-charcoal/40 border-y border-white/5" data-testid="reach-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16"
        >
          <div>
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-signal">
              Global Reach
            </span>
            <h2 className="mt-3 font-head font-extrabold tracking-tight text-base md:text-lg text-slate-300 max-w-xl leading-relaxed">
              Real places. Real people. Real culture — one upload airs on every major screen on Earth.
            </h2>
            <p className="mt-4 text-sm text-slate-500 leading-relaxed max-w-xl" data-testid="reach-copy">
              Launchpad TV is a creator-first connected TV network with CTV distribution across 193
              countries. One upload reaches 200M+ connected TV homes on every major streaming platform —
              a single global channel for culture-led programming, FAST channel partners and connected
              TV advertisers.
            </p>
          </div>
          <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-slate-500">
            LIVE SATELLITE FEED // 200M HOMES
          </span>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10" aria-label="Launchpad TV reach metrics">
          {METRICS.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="bg-obsidian p-8 lg:p-10 group hover:bg-panel transition-colors duration-500"
            >
              <CountUp to={m.value} suffix={m.suffix} testid={m.testid} />
              <div className="mt-3 font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 group-hover:text-slate-300 transition-colors duration-500">
                {m.label}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.2 }}
          className="mt-12 flex flex-wrap gap-3"
          data-testid="platform-grid"
        >
          {PLATFORMS.map((p) => (
            <span
              key={p}
              className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate-400 border border-white/10 px-4 py-2.5 hover:border-signal/60 hover:text-white transition-colors duration-300 cursor-default"
            >
              {p}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
